"use client";
import { useMemo, useState } from "react";
import type { SeriesExplorerComponent } from "../../types";
import { Search } from "lucide-react";
import { useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";

type Series = { id: string; title: string; category: string; frequency: "mensuel" | "trimestriel" | "annuel" };

/** Statistical series explorer renderer with category and frequency filters. */
export function SeriesExplorerRenderer({ component }: { component: SeriesExplorerComponent }) {
  const locale = useLocale();
  const content = component.content ?? {};
  const SERIES: Series[] = (content as any).series ?? [];
  const defaultCategory = (content as any).defaultCategory ?? "all";
  const defaultFrequency = (content as any).defaultFrequency ?? "all";
  const searchPlaceholder = (content as any).searchPlaceholder ?? t(locale, "searchSeries");

  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>(defaultCategory);
  const [freq, setFreq] = useState<string>(defaultFrequency);

  const categories = useMemo(() => ["all", ...Array.from(new Set(SERIES.map((s) => s.category)))], []);
  const freqs = ["all", "mensuel", "trimestriel", "annuel"] as const;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return SERIES.filter((s) => {
      const matchCat = category === "all" || s.category === category;
      const matchFreq = freq === "all" || s.frequency === freq;
      const matchText = !term || s.title.toLowerCase().includes(term) || s.category.toLowerCase().includes(term);
      return matchCat && matchFreq && matchText;
    });
  }, [q, category, freq]);

  return (
    <>
      <section className="mb-6 grid gap-3 md:mb-8 md:grid-cols-[1fr,220px,220px]">
        <div className="relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full rounded-md border border-slate-300 pl-10 pr-3 text-sm xl:text-base outline-none focus:border-[#00A356]"
            placeholder={searchPlaceholder}
            aria-label={t(locale, "search")}
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        </div>
        <div>
          <label className="sr-only" htmlFor="category">Catégorie</label>
          <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm xl:text-base outline-none focus:border-[#00A356]">
            {categories.map((c) => (
              <option key={c} value={c}>{c === "all" ? t(locale, "allCategories") : c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="sr-only" htmlFor="freq">Fréquence</label>
          <select id="freq" value={freq} onChange={(e) => setFreq(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm xl:text-base outline-none focus:border-[#00A356]">
            {freqs.map((f) => (
              <option key={f} value={f}>{f === "all" ? t(locale, "allFrequencies") : f}</option>
            ))}
          </select>
        </div>
      </section>
      <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white ring-1 ring-black/5">
        {filtered.map((s) => (
          <li key={s.id} className="grid gap-2 p-4 sm:grid-cols-[1fr,200px,160px] sm:items-center">
            <div>
              <h2 className="text-[15px] xl:text-[17px] font-medium text-[#121212]">{s.title}</h2>
              <p className="mt-1 text-xs xl:text-sm text-slate-500">{s.category} Â· {s.frequency}</p>
            </div>
            <div className="text-sm xl:text-base text-slate-600">{s.category}</div>
            <div className="text-sm xl:text-base text-slate-600">{s.frequency}</div>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="p-6 text-center text-slate-500">{t(locale, "noResults")}</li>
        )}
      </ul>
    </>
  );
}


